import React, { useContext, useEffect, useState } from 'react'
import { MapPin, Home, Building2 } from 'lucide-react'
import { ModernInput } from './BookingFormComponents'
import { LocationContext } from '../../context/LocationContext'
import { reqAddressLatLng } from '../../service/location'

/**
 * Address fields for markets without Singapore's postcode + OneMap lookup.
 *
 *  - places-no-postcode (UAE): area/community, building or villa, unit. The UAE
 *    has no usable postcodes; people give the community and building name.
 *  - places-postcode (Malaysia): street/area, 5-digit poskod, unit.
 *
 * Once the required fields are filled, the address is geocoded with Google
 * (the Maps script is already loaded by the booking step) to set the request's
 * lat/lng - which the non-SG flow previously never did. Geocoding is best
 * effort: if it fails, the booking continues with the city coordinates that
 * LocationContext set from the page route.
 *
 * onChange({ complete, userLocation }) fires on every edit.
 */
export const MarketAddressFields = ({ market, onChange, highlight = false }) => {
    const { setUserLatitude, setUserLongitude, city } = useContext(LocationContext)
    const noPostcode = market.addressMode === 'places-no-postcode'

    const [area, setArea] = useState('')
    const [building, setBuilding] = useState('')
    const [postcode, setPostcode] = useState('')
    const [unit, setUnit] = useState('')
    const [geocoded, setGeocoded] = useState('')

    const complete = noPostcode
        ? !!area.trim() && !!building.trim()
        : !!area.trim() && /^\d{5}$/.test(postcode)

    const userLocation = noPostcode
        ? [unit, building, area, city, market.countryName].filter((p) => p && p.trim()).join(', ')
        : [unit, area, postcode && `${postcode} ${city || ''}`.trim(), market.countryName].filter((p) => p && p.trim()).join(', ')

    useEffect(() => {
        onChange({ complete, userLocation })
    }, [complete, userLocation, onChange])

    // Geocode once the address is complete (debounced by the blur handler).
    const geocode = async () => {
        if (!complete || typeof google === 'undefined') return
        const query = noPostcode
            ? `${building}, ${area}, ${city || ''}, ${market.countryName}`
            : `${area}, ${postcode}, ${market.countryName}`
        if (query === geocoded) return
        try {
            const result = await reqAddressLatLng(query)
            setUserLatitude(result.latitude)
            setUserLongitude(result.longitude)
            setGeocoded(query)
        } catch {
            // Keep the city coordinates from the route; ops confirms the address.
        }
    }

    // Two rows, placeholders instead of labels - about the height of the
    // Singapore postal-code step, so the inline personal trainer form keeps the
    // hero's fixed height (PersonalTrainerHead min-h-205). The inputs get their
    // accessible names from ariaLabel.
    return (
        <div className="space-y-2" onBlur={geocode}>
            <ModernInput
                type="text"
                ariaLabel={noPostcode ? 'Area or community' : 'Street and area'}
                placeholder={noPostcode ? 'Area or community, e.g. Dubai Marina' : 'Street and area, e.g. Jalan Kiara 3'}
                value={area}
                onChange={(e) => setArea(e.target.value)}
                icon={MapPin}
                required
                highlight={highlight && !area.trim()}
            />
            <div className="grid grid-cols-[1fr_7rem] gap-2">
                {noPostcode ? (
                    <ModernInput
                        type="text"
                        ariaLabel="Building or villa"
                        placeholder="Building or villa"
                        value={building}
                        onChange={(e) => setBuilding(e.target.value)}
                        icon={Building2}
                        required
                        highlight={highlight && !!area.trim() && !building.trim()}
                    />
                ) : (
                    <ModernInput
                        type="text"
                        ariaLabel="Poskod"
                        placeholder="Poskod"
                        value={postcode}
                        onChange={(e) => {
                            const v = e.target.value
                            if (v === '' || /^\d{0,5}$/.test(v)) setPostcode(v)
                        }}
                        icon={MapPin}
                        minLength={5}
                        maxLength={5}
                        required
                        highlight={highlight && !!area.trim() && postcode.length !== 5}
                    />
                )}
                <ModernInput
                    type="text"
                    ariaLabel={noPostcode ? 'Apartment or villa no.' : 'Unit or house no.'}
                    placeholder={noPostcode ? 'Apt no.' : 'Unit no.'}
                    value={unit}
                    onChange={(e) => setUnit(e.target.value)}
                    icon={Home}
                />
            </div>
        </div>
    )
}
